import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { createCheckoutSession, getSubscriptionStatus } from '../services/parentApi';

export default function Upgrade() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ is_premium: boolean } | null>(null);
  const [error, setError] = useState('');

  const success = searchParams.get('success') === 'true';
  const canceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    if (id) loadStatus();
  }, [id]);

  async function loadStatus() {
    try {
      const data = await getSubscriptionStatus(id!);
      setStatus(data);
    } catch (err: any) {
      console.error(err);
    }
  }

  async function handleUpgrade() {
    if (!id) return;
    setLoading(true);
    setError('');

    try {
      const { checkout_url } = await createCheckoutSession(id);
      window.location.href = checkout_url;
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }

  if (success || status?.is_premium) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <span className="text-6xl">🎉</span>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Premium activé !</h1>
          <p className="mt-2 text-slate-600">
            Votre enfant bénéficie maintenant de 1000 crédits par mois.
          </p>
          <Link
            to={`/child/${id}`}
            className="mt-6 inline-block px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retour au profil
          </Link>
        </div>
      </div>
    );
  }

  if (canceled) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <span className="text-6xl">😕</span>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Paiement annulé</h1>
          <p className="mt-2 text-slate-600">
            Le paiement a été annulé. Vous pouvez réessayer quand vous voulez.
          </p>
          <div className="mt-6 space-x-4">
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="px-6 py-3 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Chargement...' : 'Réessayer'}
            </button>
            <Link
              to={`/child/${id}`}
              className="px-6 py-3 text-slate-600 font-medium hover:text-slate-900"
            >
              Retour
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <Link to={`/child/${id}`} className="text-primary-600 hover:text-primary-700 text-sm mb-4 inline-block">
        ← Retour au profil
      </Link>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
        <div className="text-center mb-8">
          <span className="text-5xl">⭐</span>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Passer Premium</h1>
          <p className="mt-2 text-slate-600">
            Débloquez tout le potentiel de Methodix pour votre enfant
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 mb-6">
          <div className="flex items-baseline justify-center mb-4">
            <span className="text-4xl font-bold text-amber-900">4,99€</span>
            <span className="text-amber-700 ml-2">/mois</span>
          </div>

          <ul className="space-y-3 text-amber-900">
            <li className="flex items-center">
              <span className="mr-2">✓</span>
              1000 crédits/mois (au lieu de 100)
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span>
              Accès illimité au coach IA
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span>
              Création de fiches de révision illimitée
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span>
              Support prioritaire
            </li>
          </ul>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full py-3 px-4 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Redirection vers le paiement...' : 'Souscrire maintenant'}
        </button>

        <p className="mt-4 text-center text-xs text-slate-500">
          Paiement sécurisé par Stripe. Résiliable à tout moment.
        </p>
      </div>
    </div>
  );
}

